import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { Team, TeamMember as __TeamMember } from '../types/auth.types';
import { toast } from 'sonner';
import { loadAllSettings } from '../services/settingsService';
import { notifyCategoriesUpdated } from '../constants/settings';
import { logger } from '../lib/logger';

interface TeamContextType {
  teams: Team[];
  currentTeam: Team | null;
  loading: boolean;
  createTeam: (name: string, slug: string) => Promise<{ error: unknown }>;
  switchTeam: (teamId: string) => void;
  refreshTeams: () => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export function TeamProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);

  // #140: which account `teams` / `currentTeam` currently belong to.
  //
  // A ref rather than state on purpose: it is read inside `refreshTeams` to decide
  // whether this fetch is a refresh of the account already on screen or a switch to
  // a different one, and it must carry the value written by the previous fetch
  // rather than the one captured when this closure was created.
  const loadedForUserId = useRef<string | undefined>(undefined);

  const refreshTeams = async () => {
    if (!user) {
      setTeams([]);
      setCurrentTeam(null);
      loadedForUserId.current = undefined;
      setLoading(false);
      return;
    }

    // #99: back to true before a fetch that has nothing on screen yet.
    // #140: and *only* then.
    //
    // The effect below runs on the user id, so on a fresh login it fires twice: once
    // with user === null, which takes the branch above and reports loading:false
    // having loaded nothing, and again when the user arrives. Without this the second
    // pass never turned loading back on, so for the whole length of that fetch the
    // context said "finished" while currentTeam was still null.
    //
    // Nine hooks open with `if (!currentTeam) throw new Error('No active team')`.
    // Anything the user did in that window hit one of them and got a raw English
    // string in a Hebrew UI. Reproduced on the first project creation in 2 of 5
    // fresh sessions, and never once in the 125 attempts that followed inside those
    // same sessions — by then the fetch had long finished.
    //
    // A loading flag that reports done while work is pending is worse than no flag,
    // because callers trust it. AppRoutes gates the authenticated app on this.
    //
    // #140 narrows *when* it fires, and does not weaken that guarantee. The flag
    // exists to say "there is no usable team yet". When the same account already has
    // a resolved team — a rename in TeamSettings, or any other in-app refresh — that
    // sentence is false: every `if (!currentTeam) throw` passes throughout, and
    // flipping the flag only tears the authenticated tree down and back up, taking
    // any open form's local state with it.
    //
    // The two branches are paired, and the pairing is the tenant boundary: a fetch
    // for a *different* account clears the previous account's team in the same commit
    // that raises the flag, so "loading" and "the team on screen belongs to someone
    // else" can never both be observable.
    const switchingAccount = loadedForUserId.current !== user.id;
    if (switchingAccount) {
      setTeams([]);
      setCurrentTeam(null);
      setLoading(true);
    } else if (!currentTeam) {
      setLoading(true);
    }

    try {
      const { data: teamMembers, error } = await supabase
        .from('team_members')
        .select(
          `
          team_id,
          team:teams (*)
        `
        )
        .eq('user_id', user.id);

      if (error) throw error;

      type TeamWithSoftDelete = Team & { deleted_at?: string | null };
      type TeamMemberRow = {
        team_id: string;
        team: TeamWithSoftDelete | null;
      };
      const userTeams = (teamMembers as unknown as TeamMemberRow[])
        .map(tm => tm.team)
        .filter((t): t is TeamWithSoftDelete => Boolean(t) && !t?.deleted_at);

      setTeams(userTeams);
      loadedForUserId.current = user.id;

      if (userTeams.length > 0) {
        const lastTeamId = localStorage.getItem('cpq_last_team_id');
        const lastTeam = userTeams.find(t => t.id === lastTeamId);

        if (lastTeam) {
          setCurrentTeam(lastTeam);
        } else if (
          // #140: `currentTeam` here is the value captured when this closure was
          // built, so on an account switch it can still be the previous account's
          // team even though the state was cleared above. `switchingAccount` says
          // so outright rather than relying on the id lookup below to miss.
          switchingAccount ||
          !currentTeam ||
          !userTeams.find(t => t.id === currentTeam.id)
        ) {
          setCurrentTeam(userTeams[0]);
        }
      } else {
        setCurrentTeam(null);
      }
    } catch (err: unknown) {
      console.error('Error fetching teams:', err);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  // #140: keyed on the user *id*, never on the user object.
  //
  // Supabase hands `onAuthStateChange` a freshly parsed `session.user` on every
  // silent token refresh, and a browser tab regaining focus is exactly when it
  // performs one. AuthContext passes that new reference straight through, so keyed
  // on the object this effect re-ran for the *same* signed-in account — refetching
  // the teams (the "it takes ages" half of #140) and, before the change above,
  // flipping `loading` so AppRoutes unmounted the whole authenticated tree and every
  // open form inside it.
  //
  // #99's double-fire on a fresh login is preserved unchanged: `user?.id` goes
  // undefined -> id, so the effect still runs once with no user and once with one.
  // A real account switch changes the id, so it still reloads teams in full.
  useEffect(() => {
    refreshTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (currentTeam) {
      localStorage.setItem('cpq_last_team_id', currentTeam.id);
      // Notify non-React consumers (module-scoped services like claudeAI.ts
      // and productAnalyzer.ts) that the active team is known. Fires on
      // both initial resolution and switches — unlike team-switched, which
      // only fires from switchTeam(). Consumers use this to reload
      // team-scoped settings such as the Anthropic API key.
      window.dispatchEvent(
        new CustomEvent('cpq-current-team-resolved', {
          detail: { teamId: currentTeam.id },
        })
      );
    }
  }, [currentTeam]);

  // Eagerly populate the team-scoped settings cache on mount/team-switch so
  // synchronous readers (getComponentCategories, getTableColumnSettings,
  // getDefaultQuotationParameters) see team values instead of falling back to
  // hard-coded defaults until the user visits the settings page.
  useEffect(() => {
    if (!currentTeam?.id) return;
    let cancelled = false;
    loadAllSettings(currentTeam.id)
      .then(result => {
        if (cancelled) return;
        if (result.success) {
          notifyCategoriesUpdated();
        }
      })
      .catch(err => {
        logger.error('Failed to preload team settings cache:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [currentTeam?.id]);

  const createTeam = async (name: string, slug: string) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      // The teams AFTER INSERT trigger (add_team_creator_as_admin) creates
      // the creator's team_members row in the same transaction, so PostgREST's
      // SELECT-on-returning passes and no follow-up insert is needed here.
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .insert([{ name, slug, created_by: user.id }])
        .select()
        .single();

      if (teamError) throw teamError;

      await refreshTeams();
      if (team) setCurrentTeam(team);

      return { error: null };
    } catch (err: unknown) {
      logger.error('Error creating team:', err);
      toast.error('Failed to create team');
      return { error: err };
    }
  };

  const switchTeam = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (team) {
      const oldTeamName = currentTeam?.name;

      // Clear old team's cache from localStorage (don't be too aggressive)
      if (currentTeam && currentTeam.id !== teamId) {
        const oldCacheKey = `cpq-settings-cache-${currentTeam.id}`;
        localStorage.removeItem(oldCacheKey);
      }

      setCurrentTeam(team);

      // Notify components that team switched
      window.dispatchEvent(
        new CustomEvent('team-switched', {
          detail: { teamId: team.id, fromTeam: currentTeam?.id },
        })
      );

      // Show feedback toast
      if (oldTeamName) {
        toast.success(`Switched to ${team.name}`, {
          description: `You are now viewing ${team.name}'s data and settings`,
        });
      }

      // Navigate to home page on team switch (better UX)
      setTimeout(() => {
        navigate('/');
      }, 300);
    }
  };

  const value = {
    teams,
    currentTeam,
    loading,
    createTeam,
    switchTeam,
    refreshTeams,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}
